import {useEffect, useState} from 'react';
import {useToast} from '@sail/ui';
import usePayinSessionQuery from 'src/api/graphql/usePayinSessionQuery';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {isMobile} from 'src/utils/utils';
import {notifyInContextComplete} from 'src/v2/util/inContextCompletion';
import {urlRedirect} from '@stripe-internal/safe-links';
import {formatCurrencyAmountToMajorUnitsFixed} from 'src/utils/helpers';
import {getStore} from 'src/v2/state/store';
import {getLogger} from 'src/utils/logger';

const POLL_INTERVAL_MS = 2000;

const logger = getLogger();

export const useDirectDepositPaymentPolling = (
  paymentMethod: string,
): {
  startPolling: () => void;
} => {
  const store = getStore();
  const toast = useToast();
  const [isPolling, setIsPolling] = useState(false);
  const {clientSecret, sessionId} = useSessionContext();
  const returnUrl = store.useSelector(
    (state) => state.paymentDetails.returnUrl,
  );

  const payinSessionData = usePayinSessionQuery({
    variables: {
      input: {
        crypto_payin_session: sessionId,
        client_secret: clientSecret,
      },
    },
    skip: !isPolling || !sessionId || !clientSecret,
    pollInterval: POLL_INTERVAL_MS,
    component: 'useDirectDepositPaymentPolling',
  });

  const autoRedirectDisabled = isMobile();

  const startPolling = () => {
    setIsPolling(true);
  };

  // Each time payinSessionData is polled, check if the session is successful or failed or errored
  // If so, stop polling and redirect to the appropriate screen
  useEffect(() => {
    const payinSession = payinSessionData?.data?.v1CryptoInternalPayinSession;

    if (isPolling && payinSession) {
      const sessionState = payinSession.state;

      store.setValue((state) => {
        state.state = payinSession.state;
        state.transactionDetails.blockchainTxId =
          payinSession.transaction_details?.blockchain_tx_id ?? null;
      });

      const enableUnderpaymentDetection =
        store.getState().featureFlags.enable_underpayment_detection;
      const underpaymentTransfers = payinSession.underpayment_transfers ?? [];
      const previousCount =
        store.getState().directDeposit.underpaymentTransferCount;
      if (
        enableUnderpaymentDetection &&
        underpaymentTransfers.length > previousCount
      ) {
        const newTransfers = underpaymentTransfers.slice(previousCount);
        for (const transfer of newTransfers) {
          const formattedAmount = formatCurrencyAmountToMajorUnitsFixed(
            transfer.received_amount,
            transfer.currency,
          );
          toast.show({
            type: 'neutral',
            message: `An underpayment of ${formattedAmount} ${transfer.currency.toUpperCase()} was received and will be refunded shortly.`,
          });
        }
        store.setValue((state) => {
          state.directDeposit.underpaymentTransferCount =
            underpaymentTransfers.length;
        });
      }

      if (sessionState === 'fulfillment_complete') {
        // If this is being rendered in-context, we render success screen
        // We can't automatically redirect on mobile devices, we render success screen instead
        if (notifyInContextComplete() || autoRedirectDisabled) {
          store.setValue((state) => {
            state.screen = 'SUCCESS';
          });
        } else {
          // We won't render success screen when redirect. Emit a log event to track the completion.
          logger.log('crypto_payin_session.screen.rendered', {
            screen: 'SUCCESS',
            message: `redirect for ${paymentMethod} payin session`,
          });
          urlRedirect(returnUrl);
        }
      } else if (sessionState === 'error' || sessionState === 'canceled') {
        store.setValue((state) => {
          state.screen = sessionState === 'error' ? 'ERROR' : 'CANCELED';
        });
      }
    }
  }, [
    isPolling,
    payinSessionData,
    autoRedirectDisabled,
    store,
    toast,
    returnUrl,
    paymentMethod,
  ]);

  return {
    startPolling,
  };
};
